import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Long-form Thai guide: power of attorney for use abroad. */
const TITLE = "หนังสือมอบอำนาจไปใช้ต่างประเทศ — เขียนอย่างไรไม่ให้ถูกตีกลับ";
const DESC =
  "โครงสร้างหนังสือมอบอำนาจสำหรับใช้ต่างประเทศ ขอบเขตอำนาจที่ควรระบุ การลงนามต่อหน้าทนาย Notary การรับรองนิติกรณ์ และข้อผิดพลาดที่ทำให้ต้องทำใหม่";
const URL = "/blog/power-of-attorney-abroad-guide";
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "หนังสือมอบอำนาจไปใช้ต่างประเทศต้องรับรองอย่างไร",
    answer:
      "โดยทั่วไปผู้มอบอำนาจลงนามต่อหน้าผู้มีอำนาจรับรองลายมือชื่อ (ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร) แล้วนำไปรับรองนิติกรณ์ที่กรมการกงสุล และรับรองที่สถานทูตหรือสถานกงสุลของประเทศปลายทางถ้าปลายทางกำหนด ลำดับที่ถูกต้องขึ้นกับข้อกำหนดของหน่วยงานผู้รับเอกสาร",
  },
  {
    question: "ต้องเขียนขอบเขตอำนาจละเอียดแค่ไหน",
    answer:
      "ควรระบุการกระทำที่ผู้รับมอบอำนาจต้องทำเป็นรายข้อ เช่น ยื่นคำขอ รับเอกสารคืน ลงนามในแบบฟอร์มของหน่วยงาน ชำระค่าธรรมเนียม และรับเงินคืน หากเขียนกว้างเกินไปหน่วยงานอาจไม่รับ แต่ถ้าเขียนแคบเกินไปผู้รับมอบอำนาจจะทำขั้นตอนถัดไปไม่ได้",
  },
  {
    question: "ลงนามที่ต่างประเทศแล้วนำมาใช้ในไทยได้หรือไม่",
    answer:
      "ได้ตามเงื่อนไขของหน่วยงานผู้รับ โดยทั่วไปต้องรับรองลายมือชื่อในประเทศที่ลงนาม แล้วผ่านการรับรองตามช่องทางที่หน่วยงานไทยยอมรับ พร้อมคำแปลภาษาไทย ควรตรวจกับหน่วยงานผู้รับก่อนลงนาม เพราะการแก้ภายหลังต้องเริ่มกระบวนการใหม่ทั้งชุด",
  },
  {
    question: "ใช้แบบฟอร์มสำเร็จรูปจากอินเทอร์เน็ตได้หรือไม่",
    answer:
      "มีความเสี่ยงสูง เพราะแบบฟอร์มทั่วไปมักไม่ครอบคลุมขั้นตอนของหน่วยงานเฉพาะราย และอาจใช้ถ้อยคำที่ขัดกับข้อกำหนดของประเทศปลายทาง แนวปฏิบัติที่ปลอดภัยคือร่างตามรายการขั้นตอนจริงที่ผู้รับมอบอำนาจต้องทำ แล้วให้ผู้มีอำนาจรับรองตรวจก่อนลงนาม",
  },
  {
    question: "ต้องมีพยานหรือไม่",
    answer:
      "ขึ้นกับข้อกำหนดของหน่วยงานปลายทางและกฎหมายของประเทศนั้น บางประเทศกำหนดจำนวนพยานและคุณสมบัติของพยานไว้ชัดเจน จึงควรยืนยันก่อนนัดวันลงนาม เพื่อไม่ต้องจัดนัดใหม่",
  },
  {
    question: "หนังสือมอบอำนาจมีอายุการใช้งานหรือไม่",
    answer:
      "ตัวเอกสารอาจไม่ระบุวันสิ้นสุด แต่หน่วยงานผู้รับหลายแห่งกำหนดกรอบเวลาว่าต้องออกไม่เกินระยะเวลาหนึ่งก่อนวันใช้งาน วิธีลดความเสี่ยงคือระบุวัตถุประสงค์และช่วงเวลาการใช้อำนาจไว้ในเอกสารให้ชัดเจน",
  },
  {
    question: "เพิกถอนหนังสือมอบอำนาจทำอย่างไร",
    answer:
      "ควรจัดทำหนังสือเพิกถอนเป็นลายลักษณ์อักษร แจ้งผู้รับมอบอำนาจและหน่วยงานที่เคยยื่นเอกสารไว้ พร้อมเก็บหลักฐานการแจ้ง เพราะหน่วยงานจะยังถือว่าอำนาจเดิมมีผลจนกว่าจะได้รับแจ้ง",
  },
];

export const Route = createFileRoute("/blog/power-of-attorney-abroad-guide")({
  component: PowerOfAttorneyGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("power-of-attorney-abroad-guide", "th") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("power-of-attorney-abroad-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "หนังสือมอบอำนาจไปต่างประเทศ, power of attorney ไทย, รับรองลายมือชื่อทนาย notary, มอบอำนาจขายที่ดินจากต่างประเทศ, รับรองนิติกรณ์หนังสือมอบอำนาจ",
        },
      ],
      links: canonicalAndHreflang(URL),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "บทความ", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("power-of-attorney-abroad-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Notary Public",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const SECTIONS: Array<{ heading: string; body: string }> = [
  {
    heading: "ระบุตัวบุคคลให้ตรงเอกสารแสดงตน",
    body:
      "ชื่อ นามสกุล เลขหนังสือเดินทางหรือเลขบัตรประจำตัว และที่อยู่ ต้องตรงกับเอกสารแสดงตนที่จะแนบ หากมีการเปลี่ยนชื่อหรือคำนำหน้า ควรระบุชื่อเดิมไว้ในวงเล็บและแนบหลักฐานการเปลี่ยน เพื่อให้เจ้าหน้าที่เชื่อมโยงบุคคลได้โดยไม่ต้องสอบถามเพิ่ม",
  },
  {
    heading: "เขียนขอบเขตอำนาจเป็นรายข้อตามขั้นตอนจริง",
    body:
      "ให้ไล่ลำดับงานที่ผู้รับมอบอำนาจต้องทำจริงตั้งแต่ต้นจนจบ เช่น ยื่นคำขอ ลงนามในแบบฟอร์มของหน่วยงาน ชำระและรับคืนค่าธรรมเนียม รับเอกสารคืน และยื่นต่อหน่วยงานชั้นถัดไป การเขียนแบบครอบจักรวาลอย่างเดียวมักไม่พอ เพราะหลายหน่วยงานต้องการเห็นถ้อยคำที่ตรงกับการกระทำของตน",
  },
  {
    heading: "กำหนดวัตถุประสงค์และช่วงเวลา",
    body:
      "ระบุว่าใช้เพื่อเรื่องใดและใช้ในช่วงเวลาใด ช่วยลดข้อสงสัยเรื่องอายุเอกสาร และช่วยจำกัดความเสี่ยงของผู้มอบอำนาจไม่ให้ถูกใช้อำนาจเกินวัตถุประสงค์",
  },
  {
    heading: "จัดชั้นการรับรองให้ตรงลำดับ",
    body:
      "ลงนามต่อหน้าผู้มีอำนาจรับรองลายมือชื่อก่อน จากนั้นจึงรับรองนิติกรณ์ที่กรมการกงสุล และรับรองสถานทูตปลายทางเมื่อจำเป็น การนำเอกสารไปรับรองผิดลำดับทำให้ต้องเริ่มใหม่ตั้งแต่ชั้นแรก",
  },
  {
    heading: "เตรียมคำแปลให้ครบทุกข้อความ",
    body:
      "คำแปลต้องครอบคลุมข้อความบนตราประทับ ลายมือชื่อ และข้อความท้ายเอกสาร คงการสะกดชื่อให้ตรงกับหนังสือเดินทาง และระบุปีปฏิทินให้ชัดเจนเพื่อลดข้อสงสัยระหว่าง พ.ศ. และ ค.ศ.",
  },
  {
    heading: "เผื่อชุดสำรองและเก็บหลักฐานการยื่น",
    body:
      "หน่วยงานหลายแห่งไม่คืนชุดที่ยื่น จึงควรจัดทำเผื่ออย่างน้อยหนึ่งชุด และถ่ายภาพหรือสำเนาเก็บไว้ทุกชั้นการรับรอง เพื่อใช้อ้างอิงเมื่อต้องติดตามผล",
  },
];

const CASES: Array<{ label: string; detail: string }> = [
  {
    label: "มอบอำนาจจัดการทรัพย์สินหรือที่ดินในไทย",
    detail:
      "ต้องระบุรายละเอียดทรัพย์สินให้ชี้เฉพาะได้ และตรวจถ้อยคำกับหน่วยงานที่จะยื่นก่อนลงนาม เพราะหน่วยงานที่ดินมีข้อกำหนดถ้อยคำและแบบฟอร์มของตนเอง",
  },
  {
    label: "มอบอำนาจดำเนินการเรื่องเอกสารราชการ",
    detail:
      "เช่น ขอคัดสำเนาทะเบียนราษฎร ขอหนังสือรับรองความประพฤติ หรือยื่นนิติกรณ์ ควรเขียนให้ครอบคลุมทุกชั้นที่ต้องเดิน ไม่ใช่เฉพาะการยื่นครั้งแรก",
  },
  {
    label: "มอบอำนาจในกิจการบริษัท",
    detail:
      "ต้องสอดคล้องกับอำนาจของผู้ลงนามตามหนังสือรับรองนิติบุคคล และควรแนบหนังสือรับรองพร้อมคำแปลไปด้วย มิฉะนั้นปลายทางจะไม่สามารถตรวจอำนาจผู้ลงนามได้",
  },
  {
    label: "มอบอำนาจเรื่องคดีความ",
    detail:
      "ขอบเขตต้องแยกให้ชัดว่าครอบคลุมการดำเนินคดี การประนีประนอม หรือการรับเงินตามคำพิพากษาหรือไม่ เพราะแต่ละเรื่องมีผลผูกพันต่างกัน",
  },
];

function PowerOfAttorneyGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/" className="text-muted-foreground hover:text-foreground">
          หน้าแรก
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          บทความ
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="power-of-attorney-abroad-guide" locale="th" />
      <p className="answer-summary speakable-summary">
        หนังสือมอบอำนาจที่ถูกตีกลับส่วนใหญ่ไม่ได้ผิดที่ภาษา แต่ผิดที่ขอบเขตอำนาจไม่ครอบคลุมขั้นตอนจริง
        และลำดับการรับรองไม่ตรงกับที่หน่วยงานปลายทางกำหนด บทความนี้สรุปโครงสร้างเอกสาร
        ขอบเขตที่ควรระบุ ลำดับการรับรอง และกรณีใช้งานที่พบบ่อย เพื่อให้ร่างได้ครบในครั้งแรก
      </p>

      <h2>1. ทำไมขอบเขตอำนาจจึงสำคัญกว่าความยาวเอกสาร</h2>
      <p>
        เจ้าหน้าที่ผู้รับเอกสารจะเทียบว่า “การกระทำที่ผู้แทนกำลังทำ” ปรากฏอยู่ในเอกสารหรือไม่
        หากไม่ปรากฏ แม้เจตนาชัดเพียงใดก็ไม่สามารถอนุมานแทนได้ ในทางปฏิบัติจึงต้องเขียนไล่ลำดับงาน
        ตั้งแต่การยื่นคำขอไปจนถึงการรับเอกสารคืนและการยื่นต่อหน่วยงานชั้นถัดไป
        การเขียนครบตั้งแต่ต้นมีต้นทุนเท่าเดิม แต่ประหยัดเวลาได้หลายสัปดาห์เมื่อเทียบกับการทำใหม่
      </p>

      <h2>2. โครงสร้างเอกสารที่ควรมี</h2>
      <ol>
        {SECTIONS.map((s) => (
          <li key={s.heading}>
            <strong>{s.heading}</strong> — {s.body}
          </li>
        ))}
      </ol>

      <h2>3. กรณีใช้งานที่พบบ่อยและจุดที่ต้องระวังต่างกัน</h2>
      <div className="not-prose overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">กรณีใช้งาน</th>
              <th className="p-2 text-left">จุดที่ต้องระวัง</th>
            </tr>
          </thead>
          <tbody>
            {CASES.map((c) => (
              <tr key={c.label} className="border-b align-top">
                <td className="p-2 font-medium">{c.label}</td>
                <td className="p-2">{c.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>4. ลำดับการรับรองที่พบบ่อยที่สุด</h2>
      <ol>
        <li>ผู้มอบอำนาจลงนามต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร พร้อมแสดงเอกสารแสดงตนฉบับจริง</li>
        <li>ยื่นรับรองนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ</li>
        <li>รับรองที่สถานทูตหรือสถานกงสุลของประเทศปลายทาง เมื่อประเทศนั้นกำหนด</li>
        <li>ส่งเอกสารถึงผู้รับมอบอำนาจ พร้อมสำเนาชุดสำรองและบันทึกลำดับการรับรองที่ผ่านมาแล้ว</li>
      </ol>

      <h2>5. คำถามที่พบบ่อย</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>6. แหล่งอ้างอิงและบริการที่เกี่ยวข้อง</h2>
      <p className="text-sm">
        แหล่งอ้างอิง:{" "}
        <a href="https://www.lawyerscouncil.or.th/" target="_blank" rel="noreferrer">
          สภาทนายความในพระบรมราชูปถัมภ์
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/th/publicservice/legalization" target="_blank" rel="noreferrer">
          กรมการกงสุล กระทรวงการต่างประเทศ
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/notary" className="text-primary underline">
            บริการรับรองเอกสารโดยทนาย Notary Public
          </Link>
        </li>
        <li>
          <Link to="/blog/apostille-vs-legalization-by-country" className="text-primary underline">
            Apostille กับ Legalization ต่างกันอย่างไร — เทียบรายประเทศ
          </Link>
        </li>
        <li>
          <Link to="/blog/police-clearance-certificate-guide" className="text-primary underline">
            หนังสือรับรองความประพฤติไปใช้ต่างประเทศ — คู่มือฉบับเต็ม
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        ตรวจทานเนื้อหา ณ วันที่ {PUBLISHED} เนื้อหานี้เป็นข้อมูลทั่วไปเพื่อวางลำดับงาน
        ไม่ใช่คำวินิจฉัยแทนหน่วยงานเจ้าของเรื่อง กรุณายืนยันถ้อยคำและลำดับกับหน่วยงานผู้รับเอกสารก่อนลงนาม
      </p>
    </article>
  );
}
